// ─── Collage Motion Utilities ─────────────────────────────────
// Intent-driven motion presets for collage elements.
// Maps intent.role → motion preset → framer-motion props.

import { useReducedMotion } from 'framer-motion';
import { getMotionPreset, getAnimationOrder } from './collageIntent';

const easing = [0.22, 1, 0.36, 1];

// ─── Preset Configs ───────────────────────────────────────────

const PRESETS = {
  reveal: {
    initial: { opacity: 0, y: 20, scale: 0.97 },
    whileInView: { opacity: 1, y: 0, scale: 1 },
    transition: { duration: 1.0, ease: easing },
  },
  fade: {
    initial: { opacity: 0, scale: 0.985 },
    whileInView: { opacity: 1, scale: 1 },
    transition: { duration: 0.85, ease: easing },
  },
  stagger: {
    initial: { opacity: 0, y: 14, scale: 0.985 },
    whileInView: { opacity: 1, y: 0, scale: 1 },
    transition: { duration: 0.85, ease: easing },
  },
  drift: {
    initial: { opacity: 0, y: 8, rotate: -1 },
    whileInView: { opacity: 1, y: 0, rotate: 0 },
    transition: { duration: 1.1, ease: easing },
  },
};

// ─── Hook ─────────────────────────────────────────────────────

export function useCollageElementMotion(delayOrElement) {
  const reduced = useReducedMotion();

  // Support both legacy (numeric delay) and intent-driven (element object)
  let preset = 'stagger';
  let delay = 0;

  if (typeof delayOrElement === 'number') {
    delay = delayOrElement;
  } else if (delayOrElement && typeof delayOrElement === 'object') {
    preset = getMotionPreset(delayOrElement);
    delay = (getAnimationOrder(delayOrElement) - 1) * 0.08;
  }

  const config = PRESETS[preset] || PRESETS.stagger;

  if (reduced) {
    return {
      initial: { opacity: 0 },
      whileInView: { opacity: 1 },
      viewport: { once: true, amount: 0.18 },
      transition: { duration: 0.4, delay },
    };
  }

  return {
    initial: config.initial,
    whileInView: config.whileInView,
    viewport: { once: true, amount: 0.18 },
    transition: { ...config.transition, delay },
  };
}
